import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { Board, BillItem, ItemSelection, VKUser } from './types';
import { getBoard, getItemSelections, createItemSelection, updateItemSelection, deleteItemSelection, addParticipantByName, updateBoard, updateBoardItems, deleteBoardItem } from './lib/proxy-client';
// Real-time подписки доступны, но пока отключены для отладки
// import { subscribeToItemSelections, subscribeToParticipants, unsubscribeFromChannel } from './lib/realtime';
import { initVKApp, getOrCreateGuest, showAlert, hapticFeedback, getHashParams } from './lib/vk-bridge';
import ItemCard from './components/ItemCard';
import BoardHeader from './components/BoardHeader';
import SummaryCard from './components/SummaryCard';
import LoadingSpinner from './components/LoadingSpinner';
import EditBoard from './components/EditBoard';
import MyBoards from './components/MyBoards';
import UploadReceipt from './components/UploadReceipt';
import JoinPage from './JoinPage';
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import AboutPage from './components/AboutPage';
import './App.css';

// Компонент для страницы доски
const BoardPage: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const [board, setBoard] = useState<Board | null>(null);
  const [selections, setSelections] = useState<ItemSelection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<VKUser | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const loadBoardData = useCallback(async () => {
    if (!boardId) return;
    
    try {
      setLoading(true);
      setError(null);
      console.log('📥 Загрузка доски:', boardId);
      
      const boardData = await getBoard(boardId);
      const selectionsData = await getItemSelections(boardId);
      
      setBoard(boardData);
      setSelections(selectionsData || []);
    } catch (err: any) {
      console.error('❌ Ошибка загрузки:', err);
      setError('Не удалось загрузить данные доски');
      showAlert('Ошибка загрузки данных доски');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  const isCreator = useCallback(() => {
    // Разрешаем всем редактировать (как в Telegram-версии)
    return true;
  }, []);

  const handleEditClick = () => setIsEditMode(true);
  const handleCancelEdit = () => setIsEditMode(false);

  const handleSaveChanges = async (updatedBoard: Board, deletedItemIds: string[]) => {
    if (!boardId) return;
    
    try {
      const vkId = currentUser?.id;
      
      await updateBoard(boardId, {
        restaurant_name: updatedBoard.restaurant_name,
        address: updatedBoard.address,
        total_amount: updatedBoard.total_amount
      }, vkId);
      
      await updateBoardItems(boardId, updatedBoard.bill_items || [], vkId);

      if (deletedItemIds?.length > 0) {
        await Promise.all(deletedItemIds.map(id => deleteBoardItem(id, vkId)));
      }
      
      await loadBoardData();
      setIsEditMode(false);
      showAlert('Изменения сохранены!');
    } catch (error: any) {
      console.error('❌ Ошибка сохранения:', error);
      showAlert(`Ошибка сохранения: ${error?.message || 'Неизвестная ошибка'}`);
    }
  };

  useEffect(() => {
    const init = async () => {
      await initVKApp();
      const user = await getOrCreateGuest();
      setCurrentUser(user);
      
      if (boardId) {
        loadBoardData();
      } else {
        setError('ID доски не найден в URL');
        setLoading(false);
      }
    };
    init();
  }, [boardId, loadBoardData]);

  const handleItemClick = async (item: BillItem) => {
    if (!board || !currentUser || !boardId) return;

    let participant = board.participants?.find(p => 
      p.profile?.telegram_id === currentUser.id || 
      p.name === currentUser.first_name
    );

    if (!participant) {
      try {
        const created = await addParticipantByName(boardId, currentUser.first_name, currentUser.id);
        participant = created;
        setBoard(prev => prev ? { ...prev, participants: [...(prev.participants || []), created] } : prev);
      } catch (err) {
        console.error('Ошибка создания участника:', err);
        showAlert('Не удалось создать участника');
        return;
      }
    }

    if (!participant) {
      showAlert('Участник не найден');
      return;
    }

    try {
      const existingSelection = selections.find(
        s => s.item_id === item.id && s.participant_id === participant!.id
      );

      if (existingSelection) {
        await deleteItemSelection(existingSelection.id);
        setSelections(prev => prev.filter(s => s.id !== existingSelection.id));
        hapticFeedback.impact('light');
      } else {
        const newSelection = await createItemSelection({
          item_id: item.id,
          participant_id: participant!.id,
          board_id: boardId!
        });
        setSelections(prev => [...prev, newSelection]);
        hapticFeedback.notification('success');
      }
    } catch (err: any) {
      console.error('❌ Ошибка выбора позиции:', err);
      showAlert(`Не удалось выбрать позицию: ${err?.message || 'ошибка'}`);
      hapticFeedback.notification('error');
    }
  };

  const handleRemoveSelection = async (selectionId: string) => {
    try {
      await deleteItemSelection(selectionId);
      setSelections(prev => prev.filter(s => s.id !== selectionId));
      hapticFeedback.impact('light');
    } catch (err) {
      console.error('Ошибка удаления выбора:', err);
      showAlert('Не удалось удалить выбор');
    }
  };

  const handleUpdateQuantity = async (selectionId: string, quantity: number) => {
    if (quantity <= 0) {
      await handleRemoveSelection(selectionId);
      return;
    }
    try {
      const updatedSelection = await updateItemSelection(selectionId, { quantity });
      setSelections(prev => prev.map(s => s.id === selectionId ? updatedSelection : s));
      hapticFeedback.selection();
    } catch (err) {
      console.error('Ошибка обновления количества:', err);
      showAlert('Не удалось обновить количество');
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error || !board) {
    return (
      <div className="error-container" style={{ padding: '20px', overflowY: 'auto', maxHeight: '100vh' }}>
        <h2>❌ Ошибка</h2>
        <p style={{ fontSize: '16px', marginBottom: '20px' }}>{error || 'Доска не найдена'}</p>
        <button onClick={() => window.location.reload()} style={{ marginTop: '20px', padding: '15px 30px', fontSize: '16px' }}>
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div className="app">
      <BoardHeader board={board} isCreator={isCreator()} onEditClick={handleEditClick} />
      
      <div className="main-content">
        {isEditMode ? (
          <EditBoard board={board} onSave={handleSaveChanges} onCancel={handleCancelEdit} />
        ) : (
          <div className="items-section">
            <h3>🍽️ Позиции - кликните для выбора</h3>
            <div className="items-list">
              {board.bill_items?.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  selections={selections.filter(s => s.item_id === item.id)}
                  onRemoveSelection={handleRemoveSelection}
                  onUpdateQuantity={handleUpdateQuantity}
                  onClick={() => handleItemClick(item)}
                  currentUser={currentUser}
                  board={board}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {!isEditMode && (
        <SummaryCard board={board} selections={selections} participants={board.participants || []} />
      )}

      <Footer />
    </div>
  );
};

// Страница загрузки чека (обёртка для получения VK-пользователя)
const UploadPage: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<VKUser | null>(null);

  useEffect(() => {
    const init = async () => {
      await initVKApp();
      const user = await getOrCreateGuest();
      setCurrentUser(user);
    };
    init();
  }, []);

  return <UploadReceipt currentUser={currentUser} />;
};

// Основной App с роутингом
function App() {
  // Обрабатываем hash deep link при запуске VK Mini App
  useEffect(() => {
    const hash = getHashParams();
    if (hash.startsWith('join_')) {
      const boardId = hash.replace('join_', '');
      window.location.href = `/vk/join/${boardId}`;
    }
  }, []);

  return (
    <Router basename="/vk">
      <div style={{ paddingBottom: 72 }}>
        <Routes>
          <Route path="/board/:boardId" element={<BoardPage />} />
          <Route path="/join/:boardId" element={<JoinPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/" element={<MyBoards />} />
        </Routes>
      </div>
      <BottomNav />
    </Router>
  );
}

export default App;
